<template>
    <!-- eslint-disable vue/no-use-v-if-with-v-for -->
    <div v-show="visible"
         class="picture-preview-container">
        <div class="close"
             @click="close" />
        <div ref="picturePreviewWrapper"
             tabindex="-1"
             class="picture-preview-wrapper">
            <div
                 class="picture-preview-canvas">
                <img v-for="(url, i) in urlList"
                     v-if="i === index"
                     ref="img"
                     :key="url"
                     :src="currentImg"
                     :style="imgStyle"
                     @load="handleImgLoad"
                     @error="handleImgError"
                     @mousedown="handleMouseDown"
                     @touchstart="handleMouseDownMobile"
                     @touchend="handleMouseUpMobile" />
            </div>
            <div
                v-if="actions !== 'none'"
                 class="picture-preview-action">
                <div class="action-btn icon zoom-in"
                     @click="handleActions('zoomIn')">
                </div>
                <div class="action-btn icon zoom-out"
                     @click="handleActions('zoomOut')">
                </div>
                <div
                     class="action-btn line">
                </div>
                <div class="action-btn icon download"
                     @click="handleDownload()">
                </div>
            </div>
            <div v-if="urlList.length > 1"
                 class="picture-preview-toggle">
                <div class="toggle-btn prev"
                     :class="{ 'is-disabled': !infinite && isFirst }"
                     @click="handlePrev" />
                <div class="toggle-imgs"
                     ref="imgsContainer"
                     :class="{
                        'mask-left': hasLeftOverflow,
                        'mask-right': hasRightOverflow,
                        'mask-both': hasLeftOverflow && hasRightOverflow,
                    }">
                    <img class="img-item"
                         v-for="(url, i) in urlList"
                         :src="url"
                         alt=""
                         :key="i"
                         :class="{ active: i === index }"
                         @click="handleImgClick(i)" />
                </div>
                <div class="toggle-btn next"
                     :class="{ 'is-disabled': !infinite && isLast }"
                     @click="handleNext" />
            </div>
        </div>
    </div>
</template>

<script>
import Vue from 'vue'

const isServer = Vue.prototype.$isServer
const Mode = {
    CONTAIN: {
        name: 'contain',
    },
    ORIGINAL: {
        name: 'original',
    },
}

const isFirefox = function () {
    return (
        !Vue.prototype.$isServer &&
        !!window.navigator.userAgent.match(/firefox/i)
    )
}
const mousewheelEventName = isFirefox() ? 'DOMMouseScroll' : 'mousewheel'

const on = (function () {
    if (!isServer && document.addEventListener) {
        return function (element, event, handler) {
            if (element && event && handler) {
                element.addEventListener(event, handler, false)
            }
        }
    }
    return function (element, event, handler) {
        if (element && event && handler) {
            element.attachEvent(`on${event}`, handler)
        }
    }
})()

const off = (function () {
    if (!isServer && document.removeEventListener) {
        return function (element, event, handler) {
            if (element && event) {
                element.removeEventListener(event, handler, false)
            }
        }
    }
    return function (element, event, handler) {
        if (element && event) {
            element.detachEvent(`on${event}`, handler)
        }
    }
})()

export default {
    name: 'PicturePreview',
    data() {
        return {
            hasLeftOverflow: false,
            hasRightOverflow: false,
            index: null,
            imgTop: 0,
            imgLeft: 0,
            imgScale: 1,
            mobileScale: 0, // 手指离开时图片的缩放比例
            clientX: 0,
            clientY: 0,
            start: [{}, {}],
            transform: {
                scale: 1,
                deg: 0,
                offsetX: 0,
                offsetY: 0,
                enableTransition: false,
            },
            mode: Mode.CONTAIN,
            infinite: true,
            loading: false,
            visible: false,
            closed: false,
        }
    },

    computed: {
        isSingle() {
            return this.urlList.length <= 1
        },
        isFirst() {
            return this.index === 0
        },
        isLast() {
            return this.index === this.urlList.length - 1
        },
        imgStyle() {
            const { scale, deg, offsetX, offsetY, enableTransition } =
                this.transform
            const style = {
                transform: `scale(${scale}) rotate(${deg}deg)`,
                transition: enableTransition ? 'transform .3s' : '',
                'margin-left': `${offsetX}px`,
                'margin-top': `${offsetY}px`,
            }
            if (this.mode === Mode.CONTAIN) {
                style.maxWidth = style.maxHeight = '70%'
            }
            return style
        },
        currentImg() {
            return this.urlList[this.index]
        },
    },

    watch: {
        currentImg() {
            this.$nextTick(() => {
                const $img = this.$refs.img[0]
                if (!$img.complete) {
                    this.loading = true
                }
            })
        },
        index: {
            handler() {
                this.reset()
                this.$nextTick(() => {
                    this.scrollToActiveImage()
                    this.checkOverflow()
                })
            },
            immediate: true,
        },
        urlList: {
            deep: true,
            handler() {
                // 当图片列表变化时，重新检查溢出
                this.$nextTick(() => {
                    this.checkOverflow()
                })
            },
        },
        closed(newVal) {
            if (newVal) {
                this.visible = false
                this.handleAfterLeave()
            }
        },
    },
    mounted() {
        this.index = this.initialIndex
        this.deviceSupportInstall()

        // add tabindex then wrapper can be focusable via Javascript
        // focus wrapper so arrow key can't cause inner scroll behavior underneath
        this.$refs.picturePreviewWrapper.focus()

        this.$nextTick(() => {
            this.checkOverflow()
            this.scrollToActiveImage()
        })

        // 添加滚动事件监听
        if (this.$refs.imgsContainer) {
            this.$refs.imgsContainer.addEventListener(
                'scroll',
                this.checkOverflow
            )
        }

        // 添加窗口大小变化监听
        window.addEventListener('resize', this.checkOverflow)
    },
    beforeDestroy() {
        // 移除事件监听，防止内存泄漏
        if (this.$refs.imgsContainer) {
            this.$refs.imgsContainer.removeEventListener(
                'scroll',
                this.checkOverflow
            )
        }
        window.removeEventListener('resize', this.checkOverflow)
    },

    methods: {
        checkOverflow() {
            if (!this.$refs.imgsContainer) return

            // 检查左侧溢出
            this.hasLeftOverflow = this.$refs.imgsContainer.scrollLeft > 0

            // 检查右侧溢出
            const scrollWidth = this.$refs.imgsContainer.scrollWidth
            const clientWidth = this.$refs.imgsContainer.clientWidth
            const scrollLeft = this.$refs.imgsContainer.scrollLeft
            this.hasRightOverflow = scrollLeft < scrollWidth - clientWidth - 1 // 减1是为了避免浮点误差
        },
        scrollToActiveImage() {
            if (
                !this.$refs.imgsContainer ||
                !this.$refs.imgsContainer.children.length
            )
                return
            // 获取当前选中的图片元素
            const activeImg = this.$refs.imgsContainer.children[this.index]
            if (!activeImg) return

            // 获取容器和图片的尺寸信息
            const containerRect =
                this.$refs.imgsContainer.getBoundingClientRect()
            const imgRect = activeImg.getBoundingClientRect()

            // 计算需要滚动的距离，使图片居中显示
            const containerCenter = containerRect.width / 2
            const imgCenter = imgRect.width / 2
            const scrollLeft =
                imgRect.left - containerRect.left - containerCenter + imgCenter

            // 使用平滑滚动API
            this.$refs.imgsContainer.scrollBy({
                left: scrollLeft,
                behavior: 'smooth',
            })
        },
        handleImgClick(i) {
            this.index = i
            this.$emit('next')
        },
        getMimeType(ext) {
            const mimeTypes = {
                jpg: 'image/jpeg',
                jpeg: 'image/jpeg',
                png: 'image/png',
                gif: 'image/gif',
                webp: 'image/webp',
                bmp: 'image/bmp',
                tiff: 'image/tiff',
                tif: 'image/tiff',
            }
            return mimeTypes[ext] || null
        },
        rafThrottle(fn) {
            let locked = false
            return function (...args) {
                if (locked) return
                locked = true
                window.requestAnimationFrame(() => {
                    fn.apply(this, args)
                    locked = false
                })
            }
        },
        reset() {
            this.transform = {
                scale: 1,
                deg: 0,
                offsetX: 0,
                offsetY: 0,
                enableTransition: false,
            }
        },
        handlePrev() {
            if (this.isFirst && !this.infinite) return
            const len = this.urlList.length
            this.index = (this.index - 1 + len) % len
            this.$emit('prev')
        },
        handleNext() {
            if (this.isLast && !this.infinite) return
            const len = this.urlList.length
            this.index = (this.index + 1) % len
            this.$emit('next')
        },
        toggleMode() {
            if (!navigator.onLine) return
            if (this.loading) return
            const modeNames = Object.keys(Mode)
            const modeValues = Object.values(Mode)
            const index = modeValues.indexOf(this.mode)
            const nextIndex = (index + 1) % modeNames.length
            this.mode = Mode[modeNames[nextIndex]]
            this.reset()
        },
        handleDownload() {
            const url = this.urlList[this.index]
            const a = document.createElement('a')
            a.href = url
            a.download = ''
            // 触发下载
            document.body.appendChild(a)
            a.click()

            // 清理资源
            setTimeout(() => {
                document.body.removeChild(a)
                URL.revokeObjectURL(url)
            }, 0)
            this.$emit('download')

            // // 创建 Image 对象
            // const img = new Image()
            // img.crossOrigin = 'Anonymous' // 处理跨域图片

            // img.onload = function () {
            //     // 创建 Canvas 元素
            //     const canvas = document.createElement('canvas')
            //     const ctx = canvas.getContext('2d')

            //     // 设置 Canvas 尺寸与图片一致
            //     canvas.width = img.width
            //     canvas.height = img.height

            //     // 将图片绘制到 Canvas 上
            //     ctx.drawImage(img, 0, 0, img.width, img.height)

            //     // 将 Canvas 内容转换为 Blob
            //     canvas.toBlob(
            //         blob => {
            //             if (!blob) return

            //             // 创建下载链接
            //             const url = URL.createObjectURL(blob)
            //             const a = document.createElement('a')
            //             a.href = url
            //             a.download = ''

            //             // 触发下载
            //             document.body.appendChild(a)
            //             a.click()

            //             // 清理资源
            //             setTimeout(() => {
            //                 document.body.removeChild(a)
            //                 URL.revokeObjectURL(url)
            //             }, 0)
            //         },
            //         'image/jpeg',
            //         1
            //     ) // 最后一个参数是图片质量（0-1）
            // }

            // img.onerror = function () {
            //     alert('图片加载失败，请检查图片地址是否正确')
            // }

            // img.src = url
        },
        handleActions(action, options = {}) {
            if (this.loading) return
            const { zoomRate, rotateDeg, enableTransition } = {
                zoomRate: 0.4,
                rotateDeg: 90,
                enableTransition: true,
                ...options,
            }
            const { transform } = this
            switch (action) {
                case 'zoomOut':
                    if (transform.scale > 0.2) {
                        transform.scale = parseFloat(
                            (transform.scale - zoomRate).toFixed(3)
                        )
                    }
                    this.$emit('zoomOut', transform.scale)
                    break
                case 'zoomIn':
                    if (transform.scale >= 3) return
                    transform.scale = parseFloat(
                        (transform.scale + zoomRate).toFixed(3)
                    )
                    this.$emit('zoomIn', transform.scale)
                    break
                case 'clocelise':
                    transform.deg += rotateDeg
                    break
                case 'anticlocelise':
                    transform.deg -= rotateDeg
                    break
            }
            transform.enableTransition = enableTransition
        },
        deviceSupportUninstall() {
            off(document, 'keydown', this._keyDownHandler)
            off(document, mousewheelEventName, this._mouseWheelHandler)
            this._keyDownHandler = null
            this._mouseWheelHandler = null
        },
        deviceSupportInstall() {
            this._keyDownHandler = e => {
                e.stopPropagation()
                const keyCode = e.keyCode
                switch (keyCode) {
                    // ESC
                    case 27:
                        this.close()
                        break
                    // SPACE
                    case 32:
                        this.toggleMode()
                        break
                    // LEFT_ARROW
                    case 37:
                        this.prev()
                        break
                    // UP_ARROW
                    case 38:
                        this.handleActions('zoomIn')
                        break
                    // RIGHT_ARROW
                    case 39:
                        this.next()
                        break
                    // DOWN_ARROW
                    case 40:
                        this.handleActions('zoomOut')
                        break
                }
            }
            this._mouseWheelHandler = this.rafThrottle(e => {
                const delta = e.wheelDelta ? e.wheelDelta : -e.detail
                if (delta > 0) {
                    this.handleActions('zoomIn', {
                        zoomRate: 0.015,
                        enableTransition: false,
                    })
                } else {
                    this.handleActions('zoomOut', {
                        zoomRate: 0.015,
                        enableTransition: false,
                    })
                }
            })
            on(document, 'keydown', this._keyDownHandler)
            on(document, mousewheelEventName, this._mouseWheelHandler)
        },
        handleImgLoad() {
            this.loading = false
        },
        handleImgError(e) {
            this.loading = false
            e.target.alt = '加载失败'
        },
        // 缩放，勾股定理方法-求两点之间的距离
        getDistance(p1, p2) {
            const x = p2.pageX - p1.pageX
            const y = p2.pageY - p1.pageY
            return Math.sqrt(x * x + y * y)
        },
        // 图片缩放
        scaleFunc(num, bool) {
            if (this.imgScale <= 0.2 && num < 0) return
            if (bool) {
                this.imgScale = num
            } else {
                this.imgScale += num
            }
            this.transform.scale = this.imgScale
        },
        // 手指拖动
        moveFuncMobile(e) {
            e = e || window.event
            if (e.touches.length > 1) {
                const now = e.touches
                const scale =
                    this.getDistance(now[0], now[1]) /
                    this.getDistance(this.start[0], this.start[1])
                // 判断是否手指缩放过，如果缩放过，要在上次缩放的比例基础上进行缩放
                if (this.mobileScale) {
                    if (scale > 1) {
                        // 放大
                        this.scaleFunc(scale + this.mobileScale - 1, true)
                    } else {
                        // 缩小
                        this.scaleFunc(scale * this.mobileScale, true)
                    }
                } else {
                    this.scaleFunc(scale, true)
                }
            } else {
                const touch = e.touches[0]
                e.preventDefault()
                const movementX = touch.pageX - this.clientX
                const movementY = touch.pageY - this.clientY
                this.imgLeft += movementX * 2
                this.imgTop += movementY * 2
                this.clientX = touch.pageX
                this.clientY = touch.pageY
                this.transform.offsetX = this.imgLeft
                this.transform.offsetY = this.imgTop
            }
        },
        // 手指抬起
        handleMouseUpMobile() {
            this.$refs.img[0].ontouchmove = null
            this.mobileScale = this.imgScale
        },
        // 手指按下
        handleMouseDownMobile(e) {
            e.preventDefault()
            e = e || window.event
            if (e.touches.length > 1) {
                this.start = e.touches
            } else {
                this.clientX = e.touches[0].pageX
                this.clientY = e.touches[0].pageY
            }
            // 添加手指拖动事件
            this.$refs.img[0].ontouchmove = this.moveFuncMobile
        },
        handleMouseDown(e) {
            if (this.loading || e.button !== 0) return
            const { offsetX, offsetY } = this.transform
            const startX = e.pageX
            const startY = e.pageY
            this._dragHandler = this.rafThrottle(ev => {
                this.transform.offsetX = offsetX + ev.pageX - startX
                this.transform.offsetY = offsetY + ev.pageY - startY
            })
            on(document, 'mousemove', this._dragHandler)
            on(document, 'mouseup', () => {
                off(document, 'mousemove', this._dragHandler)
            })

            e.preventDefault()
        },
        handleAfterLeave() {
            this.$destroy(true)
            this.$el.parentNode.removeChild(this.$el)
            this.deviceSupportUninstall()
        },

        close() {
            this.closed = true
            if (typeof this.onClose === 'function') {
                this.onClose(this)
            }
        },
    },
}
</script>

<style lang="scss" scoped>
.picture-preview-container {
    position: fixed;
    z-index: 99999;
    top: 0;
    left: 0;
    overflow: hidden;
    box-sizing: border-box;
    width: 100%;
    height: 100%;
    &::before {
        position: absolute;
        z-index: -1;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        content: '';
        border-radius: 6px;
        background: rgba(0, 0, 0, 0.6);
        backdrop-filter: blur(10px);
    }
    .close {
        position: absolute;
        z-index: 999;
        top: 24px;
        right: 24px;
        width: 36px;
        height: 36px;
        cursor: pointer;
        user-select: none;
        transition: all 0.1s;
        border-radius: 18px;
        background: rgba(51, 51, 51, 0.5);
        backdrop-filter: blur(10px);
        &::before {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            content: '';
            background-image: url(@/assets/images/common/picturePreview/close@2x.png);
            background-repeat: no-repeat;
            background-position: center;
            background-size: 24px 24px;
        }
        &:hover {
            background: rgba(51, 51, 51, 0.8);
        }
    }
    .picture-preview-wrapper {
        position: fixed;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        .picture-preview-canvas {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 100%;
            height: 100%;
            user-select: none;
        }
        .picture-preview-action {
            position: absolute;
            top: 24px;
            left: 50%;
            display: flex;
            align-items: center;
            padding: 6px 4px;
            user-select: none;
            transform: translateX(-50%);
            border-radius: 8px;
            background: rgba(51, 51, 51, 0.5);
            gap: 8px;
            .action-btn {
                &.icon {
                    position: relative;
                    width: 28px;
                    height: 28px;
                    cursor: pointer;
                    transition: all 0.1s;
                    border-radius: 4px;
                    &::before {
                        position: absolute;
                        top: 50%;
                        left: 50%;
                        width: 18px;
                        height: 18px;
                        content: '';
                        transform: translateX(-50%) translateY(-50%);
                        background-repeat: no-repeat;
                        background-position: center;
                        background-size: 100% 100%;
                    }
                    &.zoom-in {
                        &::before {
                            background-image: url(@/assets/images/common/picturePreview/zoomIn@2x.png);
                        }
                    }
                    &.zoom-out {
                        &::before {
                            background-image: url(@/assets/images/common/picturePreview/zoomOut@2x.png);
                        }
                    }
                    &.download {
                        &::before {
                            background-image: url(@/assets/images/common/picturePreview/download@2x.png);
                        }
                    }
                    &:hover {
                        background: rgba(51, 51, 51, 0.8);
                    }
                }
                &.line {
                    width: 1px;
                    height: 13px;
                    background: rgba(255, 255, 255, 0.2);
                }
            }
        }
        .picture-preview-toggle {
            position: absolute;
            bottom: 32px;
            left: 0;
            display: flex;
            align-items: center;
            justify-content: space-between;
            box-sizing: border-box;
            width: 100%;
            height: 66px;
            padding: 0 24px;
            user-select: none;
            .toggle-btn {
                flex: 0 0 auto;
                width: 36px;
                height: 36px;
                cursor: pointer;
                transition: all 0.1s;
                border-radius: 50%;
                background: rgba(51, 51, 51, 0.5);
                backdrop-filter: blur(10px);
                &::before {
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    width: 24px;
                    height: 24px;
                    content: '';
                    transform: translateX(-50%) translateY(-50%);
                    background-repeat: no-repeat;
                    background-position: center;
                    background-size: 100% 100%;
                }
                &.prev {
                    &::before {
                        background-image: url(@/assets/images/common/picturePreview/arrowLeft@2x.png);
                    }
                }
                &.next {
                    &::before {
                        background-image: url(@/assets/images/common/picturePreview/arrowRight@2x.png);
                    }
                }
                &.is-disabled {
                    cursor: not-allowed;
                    opacity: 0.25;
                }
                &:hover {
                    background: rgba(51, 51, 51, 0.8);
                }
            }
            .toggle-imgs {
                display: flex;
                overflow: hidden;
                align-items: center;
                box-sizing: border-box;
                max-width: calc(100% - 100px);
                height: 100%;
                padding: 2px;
                gap: 8px;
                .img-item {
                    max-width: 102px;
                    height: 100%;
                    cursor: pointer;
                    border: 2px solid transparent;
                    border-radius: 8px;
                    object-fit: cover;
                    &.active {
                        border-color: #ffffff;
                    }
                }
                &.mask-left {
                    mask-image: linear-gradient(
                        90deg,
                        transparent 0%,
                        black 20px,
                        black 100%
                    );
                    -webkit-mask-image: linear-gradient(
                        90deg,
                        transparent 0%,
                        black 20px,
                        black 100%
                    );
                }
                &.mask-right {
                    mask-image: linear-gradient(
                        90deg,
                        black 0%,
                        black calc(100% - 20px),
                        transparent 100%
                    );
                    -webkit-mask-image: linear-gradient(
                        90deg,
                        black 0%,
                        black calc(100% - 20px),
                        transparent 100%
                    );
                }
                &.mask-both {
                    mask-image: linear-gradient(
                        90deg,
                        transparent 0%,
                        black 20px,
                        black calc(100% - 20px),
                        transparent 100%
                    );
                    -webkit-mask-image: linear-gradient(
                        90deg,
                        transparent 0%,
                        black 20px,
                        black calc(100% - 20px),
                        transparent 100%
                    );
                }
            }
        }
    }
}
</style>
